// Created by Claude Code AI Agent
import { inject, Injectable } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { CurrentUserService, Permission, WarehouseManagementSettingsModel } from '@smartmoving/sm-shared-lib';
import { CrewApiClientService } from 'src/app/shared/crew-api-client.service';

export type WarehouseAccessResult = 'granted' | 'no-permission' | 'not-enabled' | 'unknown';

// The three warehouse permissions that grant access to warehouse tools.
// KEEP IN SYNC with the server gate — the AuthorizeMultiplePolicy list on
// CrewAppWarehouseManagementSettingsController (smartmoving-cloud). The API enforces these three;
// the tab/guard gate must match, or the UI will show/hide out of step with what the API allows.
export const WAREHOUSE_PERMISSIONS: Permission[] = [
  Permission.CrewCanViewWarehouse,
  Permission.CrewCanManageWarehouseInventory,
  Permission.CrewCanReleaseItems
];

// Single source of truth for Warehouse access, shared by the tab button
// (AppComponent.showWarehouseTab) and the route guard (warehouseAccessGuard) so the two can
// never diverge. The per-company warehouse toggle is queried live from the crew API on tab load
// rather than read from the cached login payload, so an admin turning it off takes effect on the
// next refresh without a re-login. The toggle already folds in the CrewAppWarehousing feature
// flag server-side; permissions ride the JWT, so only the toggle needs to be live.
@Injectable({
  providedIn: 'root'
})
export class WarehouseAccessService {
  private readonly currentUser = inject(CurrentUserService);
  private readonly crewApiClient = inject(CrewApiClientService);

  private enabled?: boolean;

  // Cached for the app session; a full page refresh re-creates the service and re-queries.
  async isWarehouseEnabled(): Promise<boolean> {
    if (this.enabled === undefined) {
      const settings = await this.crewApiClient.get<WarehouseManagementSettingsModel>('crew-app/settings/warehouse-management');
      this.enabled = settings?.enableWarehouseManagement === true;
    }
    return this.enabled;
  }

  // Forces the next isWarehouseEnabled() call to re-query — e.g. after a login switches the user.
  reset(): void {
    this.enabled = undefined;
  }

  // Permission check first (local, and the endpoint is permission-gated) so users without any
  // warehouse permission short-circuit and never call the live query.
  async hasWarehouseAccess(): Promise<boolean> {
    return await this.getAccessResult() === 'granted';
  }

  async getAccessResult(): Promise<WarehouseAccessResult> {
    if (!this.currentUser.hasAnyPermission(WAREHOUSE_PERMISSIONS)) {
      return 'no-permission';
    }

    try {
      return await this.isWarehouseEnabled() ? 'granted' : 'not-enabled';
    } catch (error) {
      return (error as HttpErrorResponse)?.status === 403 ? 'no-permission' : 'unknown';
    }
  }
}
